import "react-toastify/dist/ReactToastify.css";
import "katex/dist/katex.min.css";
import "@/styles/globals.css";

import type { AppProps } from "next/app";
import { ErrorBoundary } from "react-error-boundary";

import { Layout } from "@/layout/Layout";

import {
	QueryClient,
	QueryClientProvider,
	HydrationBoundary,
	QueryCache,
} from "@tanstack/react-query";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { defaultToastConfig } from "@/lib/reactToastify/emitToast";
import { SidemenuProvider } from "@/contexts/Sidemenu";
import { CssBaseline, ThemeProvider } from "@mui/material";
import theme from "@/styles/theme";
import { AuthProvider } from "@/contexts/Auth";
import { AbilityProvider } from "@/contexts/AbilityContext";
import { RouteGuard } from "@/components/RouteGuard";
import Head from "next/head";
import Custom500 from "./500.page";
import { useUserStore } from "@/store/useUserStore";
import { useUserOrganizationStore } from "@/store/useUserOrganizationStore";
import { getClientCookies } from "@/lib/cookies";
import { isSessionExpired } from "@/utils/cookieExpiry";
import { PUBLIC_ROUTES, ROUTES } from "@/constants/Routes";

function isPublicRoute(pathname: string): boolean {
	return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
}

export default function App({ Component, pageProps }: AppProps) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				queryCache: new QueryCache({
					onError: (error) => {
						console.error(`Global Error: ${error.message}`);
					},
				}),
				defaultOptions: {
					queries: {
						staleTime: 5 * 60 * 1000,
						gcTime: 10 * 60 * 1000,
					},
				},
			}),
	);
	const [isMounted, setIsMounted] = useState(false);
	const createdAt = getClientCookies()?.created_at;

	const resetUser = useUserStore((store) => store.reset);
	const resetUserOrganization = useUserOrganizationStore(
		(store) => store.resetAll,
	);

	useEffect(() => {
		setIsMounted(true);
		window.history.scrollRestoration = "manual";
	}, []);

	useEffect(() => {
		if (createdAt) {
			if (isSessionExpired(createdAt)) {
				resetUser();
				resetUserOrganization();

				if (!isPublicRoute(window.location.pathname)) {
					window.location.href = ROUTES.HOME;
				}
			}

			return;
		}

		resetUser();
		resetUserOrganization();

		if (!isPublicRoute(window.location.pathname)) {
			window.location.href = ROUTES.HOME;
		}
	}, [createdAt]);

	if (!isMounted) {
		return null;
	}

	return (
		<>
			<Head>
				<meta charSet='utf-8' />
				<meta name='viewport' content='width=device-width, initial-scale=1' />
				<link rel='icon' href='/favicon.ico' />
				<meta name='theme-color' content='#ffffff' />
				<title>RAIP | Plataforma Responsável de Inteligência Artificial</title>
			</Head>

			<ErrorBoundary fallback={<Custom500 />}>
				<QueryClientProvider client={queryClient}>
					<HydrationBoundary state={pageProps.dehydratedState}>
						<ThemeProvider theme={theme}>
							<CssBaseline />
							<AuthProvider>
								<AbilityProvider>
									<SidemenuProvider>
										<Layout>
											<ToastContainer {...defaultToastConfig} limit={1} />
											<RouteGuard>
												<Component {...pageProps} />
											</RouteGuard>
										</Layout>
										<ReactQueryDevtools initialIsOpen={false} />
									</SidemenuProvider>
								</AbilityProvider>
							</AuthProvider>
						</ThemeProvider>
					</HydrationBoundary>
				</QueryClientProvider>
			</ErrorBoundary>
		</>
	);
}
